import React from 'react';
import Icon from 'react-icons-kit';

// FontAwesome
import { cog } from 'react-icons-kit/fa/cog';
import { questionCircle } from 'react-icons-kit/fa/questionCircle';
import { signOut } from 'react-icons-kit/fa/signOut';
import { photo } from 'react-icons-kit/fa/photo';
import { music } from 'react-icons-kit/fa/music';
import { bold } from 'react-icons-kit/fa/bold';
import { italic } from 'react-icons-kit/fa/italic';
import { underline } from 'react-icons-kit/fa/underline';
import { strikethrough } from 'react-icons-kit/fa/strikethrough';
import { quoteRight } from 'react-icons-kit/fa/quoteRight';
import { listUl } from 'react-icons-kit/fa/listUl';
import { listOl } from 'react-icons-kit/fa/listOl';
import { eraser } from 'react-icons-kit/fa/eraser';
import { link } from 'react-icons-kit/fa/link';
import { twitterSquare } from 'react-icons-kit/fa/twitterSquare';
import { twitter } from 'react-icons-kit/fa/twitter';
import { keyboardO } from 'react-icons-kit/fa/keyboardO';
import { code } from 'react-icons-kit/fa/code';
import { circle } from 'react-icons-kit/fa/circle';
import { header } from 'react-icons-kit/fa/header';

// IcoMoon
import { quill } from 'react-icons-kit/icomoon/quill';

// Material Design
import { ic_send } from 'react-icons-kit/md/ic_send';
import { ic_help_outline } from 'react-icons-kit/md/ic_help_outline';
import { ic_close } from 'react-icons-kit/md/ic_close';
import { ic_format_quote } from 'react-icons-kit/md/ic_format_quote';
import { ic_image } from 'react-icons-kit/md/ic_image';
import { ic_link } from 'react-icons-kit/md/ic_link';

// Streamline
import {
  Add1F,
  Remove1F,
  Picture3L,
  CameraBoxF,
  CameraBoxL,
  LayersShowL,
  NewDocumentText,
  NewDocumentLayer,
  EmailSend1,
  AddCircle1,
  MusicNote1L,
  MusicNote3L,
  CodeSnippet,
  NumberedList,
  BulletedList,
} from 'shared/static/icons';

import { mixClasses as cx } from 'helpers';


/*
 * Main entry object to all svg icons
 */
export const SvgIcon = {
  im: {},    // IcoMoon
  fa: {},    // FontAwesome
  md: {},    // Material Design
  strl: {},  // Streamline: www.streamlineicons.com
  ooff: {},  // one-off icons
};


/*
 * React Icons Kit (SVG)
 */
// Helper func to use react-icons-kit easily
const fromIconsKit = (icon) => (props) => <Icon icon={icon} {...props} />;

SvgIcon.im.Quill = fromIconsKit(quill);

SvgIcon.fa.Cog = fromIconsKit(cog);
SvgIcon.fa.QuesCircle = fromIconsKit(questionCircle)
SvgIcon.fa.SignOut = fromIconsKit(signOut);
SvgIcon.fa.Photo = fromIconsKit(photo);
SvgIcon.fa.Music = fromIconsKit(music);
SvgIcon.fa.Bold = fromIconsKit(bold);
SvgIcon.fa.Italic = fromIconsKit(italic);
SvgIcon.fa.Underline = fromIconsKit(underline);
SvgIcon.fa.Strikethru = fromIconsKit(strikethrough);
SvgIcon.fa.UnorderedList = fromIconsKit(listUl);
SvgIcon.fa.OrderedList = fromIconsKit(listOl);
SvgIcon.fa.QuoteRight = fromIconsKit(quoteRight);
SvgIcon.fa.Eraser = fromIconsKit(eraser);
SvgIcon.fa.Link = fromIconsKit(link);
SvgIcon.fa.KeyboardO = fromIconsKit(keyboardO);
SvgIcon.fa.Twitter = fromIconsKit(twitter);
SvgIcon.fa.TwitterSquare = fromIconsKit(twitterSquare);
SvgIcon.fa.Code = fromIconsKit(code);
SvgIcon.fa.Circle = fromIconsKit(circle);
SvgIcon.fa.Header = fromIconsKit(header);

SvgIcon.md.Send = fromIconsKit(ic_send);
SvgIcon.md.HelpOutline = fromIconsKit(ic_help_outline);
SvgIcon.md.Close = fromIconsKit(ic_close);
SvgIcon.md.Quote = fromIconsKit(ic_format_quote);
SvgIcon.md.Image = fromIconsKit(ic_image);
SvgIcon.md.Link = fromIconsKit(ic_link);


/*
 * Streamline icons (SVG)
 *
 * L: line
 * F: filled
 */
// Streamline svg icons (from shared/static/icons/) are run
// through react-svg-loader to turn into react component
// automatically with props.
// Reference: github.com/boopathi/react-svg-loader
const fromInlineSvg = InlineSvgComp => ({ size = 16, ...props }) => (
  <InlineSvgComp width={size} height={size} {...props} />
);


SvgIcon.strl.Remove1F = fromInlineSvg(Remove1F);
SvgIcon.strl.Add1F = fromInlineSvg(Add1F);
SvgIcon.strl.Picture3L = fromInlineSvg(Picture3L);
SvgIcon.strl.CameraBoxF = fromInlineSvg(CameraBoxF);
SvgIcon.strl.CameraBoxL = fromInlineSvg(CameraBoxL);
SvgIcon.strl.LayersShowL = fromInlineSvg(LayersShowL);
SvgIcon.strl.NewDocumentText = fromInlineSvg(NewDocumentText);
SvgIcon.strl.NewDocumentLayer = fromInlineSvg(NewDocumentLayer);
SvgIcon.strl.EmailSend1 = fromInlineSvg(EmailSend1);
SvgIcon.strl.AddCircle1 = fromInlineSvg(AddCircle1);
SvgIcon.strl.MusicNote1L = fromInlineSvg(MusicNote1L);
SvgIcon.strl.MusicNote3L = fromInlineSvg(MusicNote3L);

SvgIcon.ooff.CodeSnippet = fromInlineSvg(CodeSnippet);
SvgIcon.ooff.NumberedList = fromInlineSvg(NumberedList);
SvgIcon.ooff.BulletedList = fromInlineSvg(BulletedList);


/*
 * Font icon
 */
// TODO: Make the font source dynamic.
export const FontIcon = ({type, className, style, ...props}) => {
  const iconClass = `anticon icon-${type}`;

  return (
    <span
      className={cx(iconClass, className)}
      style={style}
      {...props}
    />
  );
};



// Reference:
// https://medium.com/@david.gilbertson/icons-as-react-components-de3e33cb8792



// WEBPACK FOOTER //
// ./src/components/elems/Icon.js